import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { tradingAPI } from '../../services/api';
import { useTradingToast } from '../../hooks/useTradingToast';
import { X, TrendingUp, TrendingDown, AlertTriangle, Target, Shield } from 'lucide-react';

const SLTPModal = ({ isOpen, onClose, trade, onUpdate }) => {
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(null);
  const { toast } = useTradingToast();

  useEffect(() => {
    if (trade && isOpen) {
      setStopLoss(trade.stop_loss ? trade.stop_loss.toString() : '');
      setTakeProfit(trade.take_profit ? trade.take_profit.toString() : '');
      loadCurrentPrice();
    }
  }, [trade, isOpen]);

  const loadCurrentPrice = async () => {
    if (!trade) return;
    try {
      const response = await tradingAPI.getPrice(trade.symbol);
      setCurrentPrice(response.data.price);
    } catch (error) {
      console.error('Failed to load price:', error);
    }
  };

  if (!isOpen || !trade) return null;

  const isLong = trade.side === 'long';
  const entryPrice = trade.entry_price;
  const size = (trade.margin * trade.leverage) / entryPrice;

  // Validate SL/TP
  const validateSLTP = () => {
    const sl = stopLoss ? parseFloat(stopLoss) : null;
    const tp = takeProfit ? parseFloat(takeProfit) : null;

    if (isLong) {
      if (sl && sl >= entryPrice) {
        return { valid: false, error: 'Стоп-лосс должен быть ниже цены входа для LONG позиции' };
      }
      if (tp && tp <= entryPrice) {
        return { valid: false, error: 'Тейк-профит должен быть выше цены входа для LONG позиции' };
      }
    } else {
      if (sl && sl <= entryPrice) {
        return { valid: false, error: 'Стоп-лосс должен быть выше цены входа для SHORT позиции' };
      }
      if (tp && tp >= entryPrice) {
        return { valid: false, error: 'Тейк-профит должен быть ниже цены входа для SHORT позиции' };
      }
    }

    return { valid: true };
  };

  // Calculate potential PnL
  const calculatePotentialPnL = (targetPrice) => {
    if (!targetPrice) return null;
    const priceDiff = isLong ? (targetPrice - entryPrice) : (entryPrice - targetPrice);
    return priceDiff * size;
  };

  const handleSubmit = async () => {
    const validation = validateSLTP();
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setLoading(true);
    try {
      const updateData = {
        stop_loss: stopLoss ? parseFloat(stopLoss) : null,
        take_profit: takeProfit ? parseFloat(takeProfit) : null,
      };

      await tradingAPI.updateSlTp(trade.id, updateData);
      toast.success('SL/TP успешно обновлены');
      onUpdate && onUpdate();
      onClose();
    } catch (error) {
      toast.error('Ошибка при обновлении SL/TP');
      console.error('Failed to update SL/TP:', error);
    } finally {
      setLoading(false);
    }
  };

  const slPnL = calculatePotentialPnL(parseFloat(stopLoss));
  const tpPnL = calculatePotentialPnL(parseFloat(takeProfit));

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#1e2329',
        borderRadius: '12px',
        width: '420px',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '1px solid #2b3139',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #2b3139',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '16px', fontWeight: '700', color: '#eaecef' }}>
              Установить SL/TP
            </span>
            <span style={{
              padding: '3px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '700',
              backgroundColor: isLong ? '#0ecb81' : '#f6465d',
              color: '#fff',
            }}>
              {trade.symbol} {isLong ? 'LONG' : 'SHORT'} {trade.leverage}x
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#848e9c',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Position Info */}
        <div style={{
          padding: '16px 20px',
          backgroundColor: '#0b0e11',
          borderBottom: '1px solid #2b3139',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '11px', color: '#848e9c', marginBottom: '4px' }}>Цена входа</div>
              <div style={{ fontSize: '14px', color: '#eaecef', fontFamily: 'monospace', fontWeight: '600' }}>
                ${entryPrice.toFixed(2)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#848e9c', marginBottom: '4px' }}>Текущая цена</div>
              <div style={{ fontSize: '14px', color: '#f0b90b', fontFamily: 'monospace', fontWeight: '600' }}>
                ${currentPrice?.toFixed(2) || '...'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: '#848e9c', marginBottom: '4px' }}>Размер</div>
              <div style={{ fontSize: '14px', color: '#eaecef', fontFamily: 'monospace', fontWeight: '600' }}>
                {size.toFixed(5)}
              </div>
            </div>
          </div>
        </div>

        {/* SL/TP Inputs */}
        <div style={{ padding: '20px' }}>
          {/* Stop Loss */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <Shield size={16} color="#f6465d" />
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#eaecef' }}>
                Стоп-Лосс (SL)
              </span>
              {isLong ? (
                <span style={{ fontSize: '10px', color: '#848e9c' }}>
                  Должен быть ниже {entryPrice.toFixed(2)}
                </span>
              ) : (
                <span style={{ fontSize: '10px', color: '#848e9c' }}>
                  Должен быть выше {entryPrice.toFixed(2)}
                </span>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                value={stopLoss}
                onChange={(e) => setStopLoss(e.target.value)}
                placeholder={`Цена стоп-лосса`}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: '#0b0e11',
                  border: '1px solid #2b3139',
                  borderRadius: '8px',
                  color: '#eaecef',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  outline: 'none',
                }}
              />
              {slPnL !== null && stopLoss && (
                <div style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '11px',
                  color: slPnL >= 0 ? '#0ecb81' : '#f6465d',
                  fontWeight: '600',
                }}>
                  {slPnL >= 0 ? '+' : ''}{slPnL.toFixed(2)} USDT
                </div>
              )}
            </div>
          </div>

          {/* Take Profit */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <Target size={16} color="#0ecb81" />
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#eaecef' }}>
                Тейк-Профит (TP)
              </span>
              {isLong ? (
                <span style={{ fontSize: '10px', color: '#848e9c' }}>
                  Должен быть выше {entryPrice.toFixed(2)}
                </span>
              ) : (
                <span style={{ fontSize: '10px', color: '#848e9c' }}>
                  Должен быть ниже {entryPrice.toFixed(2)}
                </span>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                value={takeProfit}
                onChange={(e) => setTakeProfit(e.target.value)}
                placeholder={`Цена тейк-профита`}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: '#0b0e11',
                  border: '1px solid #2b3139',
                  borderRadius: '8px',
                  color: '#eaecef',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  outline: 'none',
                }}
              />
              {tpPnL !== null && takeProfit && (
                <div style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '11px',
                  color: tpPnL >= 0 ? '#0ecb81' : '#f6465d',
                  fontWeight: '600',
                }}>
                  {tpPnL >= 0 ? '+' : ''}{tpPnL.toFixed(2)} USDT
                </div>
              )}
            </div>
          </div>

          {/* Info */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            padding: '12px',
            backgroundColor: '#f0b90b15',
            borderRadius: '8px',
            marginBottom: '20px',
          }}>
            <AlertTriangle size={16} color="#f0b90b" style={{ flexShrink: 0, marginTop: '2px' }} />
            <span style={{ fontSize: '11px', color: '#848e9c', lineHeight: '1.5' }}>
              Ордера SL/TP будут исполнены автоматически при достижении указанной цены. 
              Оставьте поле пустым чтобы удалить ордер.
            </span>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: 'transparent',
                border: '1px solid #2b3139',
                borderRadius: '8px',
                color: '#848e9c',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Отмена
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: '#f0b90b',
                border: 'none',
                borderRadius: '8px',
                color: '#0b0e11',
                fontSize: '14px',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SLTPModal;
